import { authService } from './auth';

interface Comment {
  id: string;
  user_id: string;
  username: string; // バックエンドで動的に取得された最新のユーザー名
  avatar_url: string | null; // バックエンドで生成されたプリサインドURL
  content: string;
  created_at: string;
  updated_at: string;
}

interface CommentRequest {
  content: string;
}

interface CommentsResponse {
  comments: Comment[];
  hasMore: boolean;
  lastKey?: string;
}

class CommentService {
  async getComments(
    photoId: string,
    limit: number = 20,
    lastKey?: string
  ): Promise<CommentsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
    });

    if (lastKey) {
      params.append('lastKey', lastKey);
    }

    const response = await authService.authFetch(
      `/photos/${photoId}/comments?${params}`
    );

    if (!response.ok) {
      throw new Error('コメントの取得に失敗しました');
    }

    return await response.json();
  }

  async createComment(photoId: string, content: string): Promise<Comment> {
    const request: CommentRequest = { content };

    const response = await authService.authFetch(
      `/photos/${photoId}/comments`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        invalidateCache: [
          `/photos/${photoId}/comments/count`,
          `/photos/${photoId}/comments`,
        ],
      }
    );

    if (!response.ok) {
      throw new Error('コメントの投稿に失敗しました');
    }

    return await response.json();
  }

  async updateComment(commentId: string, content: string): Promise<Comment> {
    const request: CommentRequest = { content };

    const response = await authService.authFetch(`/comments/${commentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      invalidateCache: '/comments', // コメント関連キャッシュを無効化
    });

    if (!response.ok) {
      throw new Error('コメントの更新に失敗しました');
    }

    return await response.json();
  }

  async deleteComment(commentId: string): Promise<void> {
    const response = await authService.authFetch(`/comments/${commentId}`, {
      method: 'DELETE',
      invalidateCache: '/comments', // コメント関連キャッシュを無効化
    });

    if (!response.ok) {
      throw new Error('コメントの削除に失敗しました');
    }
  }

  async getCommentCount(photoId: string): Promise<number> {
    const response = await authService.authFetch(
      `/photos/${photoId}/comments/count`,
      {
        useCache: true,
        ttl: 2 * 60 * 1000, // 2分間キャッシュ
      }
    );

    if (!response.ok) {
      throw new Error('コメント数の取得に失敗しました');
    }

    const data = await response.json();
    return data.count;
  }
}

export const commentService = new CommentService();
export type { Comment, CommentsResponse };
